import { Aurora } from "@/components/Aurora";

/**
 * IUL Motion Graphic — IUL line only rises or flatlines. Adds clear narrative
 * callouts so the story is unmistakable: market drops, IUL locks in, captures gains.
 */
export const IULMotionGraphic = () => {
  return (
    <div className="relative w-full">
      <div className="absolute -inset-6 rounded-[2.5rem] bg-breeze-gradient opacity-30 blur-3xl" aria-hidden="true" />
      <div className="relative rounded-[2rem] overflow-hidden bg-[#0B132B] text-white border border-white/10 shadow-2xl shadow-[#0B132B]/40">
        <div className="relative">
          <Aurora intensity="intense" />
          <div className="relative z-10 p-6 md:p-8">
            <div className="mb-5">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur ring-1 ring-white/15 px-3.5 py-1.5 text-sm font-semibold text-white">
                <span className="w-2 h-2 rounded-full bg-[#29ABE2]" />
                Indexed protection
              </span>
            </div>

            <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
              <defs>
                <linearGradient id="iul-line" x1="0" x2="1">
                  <stop offset="0%" stopColor="#4CC3F0" />
                  <stop offset="100%" stopColor="#0a6fc2" />
                </linearGradient>
                <linearGradient id="iul-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#29ABE2" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#29ABE2" stopOpacity="0" />
                </linearGradient>
                <filter id="iul-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" />
                </filter>
              </defs>

              {/* Floor */}
              <line x1="10" y1="220" x2="390" y2="220" stroke="#29ABE2" strokeWidth="1.2" strokeDasharray="5 5" opacity="0.55" />
              <text x="14" y="245" fill="#29ABE2" fontSize="16" fontWeight="800">
                Protected floor
              </text>

              {/* Market line (dips) */}
              <path
                d="M 10 220 L 75 145 L 140 235 L 205 95 L 270 230 L 335 120 L 390 95"
                fill="none"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.6"
                strokeDasharray="5 6"
                style={{ strokeDasharray: 800, strokeDashoffset: 800, animation: "iul-draw 1.6s ease-out 0.2s forwards" }}
              />
              <text x="320" y="86" fill="rgba(255,255,255,0.55)" fontSize="15" fontWeight="700">
                Market
              </text>

              {/* IUL fill */}
              <path
                d="M 10 220 L 75 170 L 140 170 L 205 115 L 270 115 L 335 65 L 390 65 L 390 220 L 10 220 Z"
                fill="url(#iul-fill)"
                style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.4s forwards" }}
              />

              {/* IUL line — only rises or flatlines */}
              <path
                d="M 10 220 L 75 170 L 140 170 L 205 115 L 270 115 L 335 65 L 390 65"
                fill="none"
                stroke="url(#iul-line)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#iul-glow)"
                style={{ strokeDasharray: 720, strokeDashoffset: 720, animation: "iul-draw 2.2s ease-out 0.8s forwards" }}
              />
              <path
                d="M 10 220 L 75 170 L 140 170 L 205 115 L 270 115 L 335 65 L 390 65"
                fill="none"
                stroke="url(#iul-line)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ strokeDasharray: 720, strokeDashoffset: 720, animation: "iul-draw 2.2s ease-out 0.8s forwards" }}
              />
              <text x="360" y="56" fill="#4CC3F0" fontSize="17" fontWeight="800">
                IUL
              </text>

              {/* Callout — "Locks in" pointing up at the first flat segment */}
              <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 2s forwards" }}>
                <text x="80" y="200" fill="#4CC3F0" fontSize="16" fontWeight="800">
                  ↑ Locks in
                </text>
              </g>

              {/* Callout — "Captures gain" near top right */}
              <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 2.6s forwards" }}>
                <text x="200" y="45" fill="#fff" fontSize="16" fontWeight="800">
                  ↗ Captures gain
                </text>
              </g>

              {/* Lock-in markers */}
              {[
                { x: 140, y: 170, delay: "1.6s" },
                { x: 270, y: 115, delay: "1.9s" },
              ].map((m) => (
                <g key={m.x}>
                  <circle cx={m.x} cy={m.y} r="5" fill="#29ABE2" style={{ opacity: 0, animation: `iul-fade 0.5s ease-out ${m.delay} forwards` }} />
                  <circle cx={m.x} cy={m.y} r="5" fill="#29ABE2">
                    <animate attributeName="r" values="5;11;5" dur="3s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.55;0;0.55" dur="3s" repeatCount="indefinite" />
                  </circle>
                </g>
              ))}

              {/* Endpoint pulse */}
              <circle cx="390" cy="65" r="6" fill="#fff" style={{ opacity: 0, animation: "iul-fade 0.5s ease-out 2.6s forwards" }} />
              <circle cx="390" cy="65" r="6" fill="#29ABE2">
                <animate attributeName="r" values="6;14;6" dur="2.6s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0;0.6" dur="2.6s" repeatCount="indefinite" />
              </circle>
            </svg>

            <div className="flex flex-wrap gap-2 mt-5">
              <span className="rounded-full bg-[#29ABE2]/15 text-[#4CC3F0] px-3.5 py-1.5 ring-1 ring-[#29ABE2]/30 text-sm font-semibold">
                Downside protection
              </span>
              <span className="rounded-full bg-white/10 text-white px-3.5 py-1.5 ring-1 ring-white/15 text-sm font-semibold">
                Tax-free growth
              </span>
              <span className="rounded-full bg-white/10 text-white px-3.5 py-1.5 ring-1 ring-white/15 text-sm font-semibold">
                Lifetime coverage
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
